<script setup>
import { VuePDF } from '@tato30/vue-pdf'
import { usePDF } from './pdf'
import { useParentElement } from '@vueuse/core'
import { toRef, useTemplateRef, ref, onMounted, onBeforeUnmount } from 'vue'
import { scrollTo } from '@/lib/dom'
import { storeToRefs } from 'pinia'
import { useUserStore } from '@/stores/user'

const emit = defineEmits(['loaded', 'page'])
const props = defineProps(['link', 'scale', 'rotation'])
/**
 * @type {IntersectionObserver} observer
 */
let observer = null
let pageLoaded = 0
let firstViewport = null
let intersecting = true
const pageCurrent = ref(1)
const dnLink = toRef(props, 'link')
const pagesCanvaEl = useTemplateRef('pages-canva')
const pageEl = useTemplateRef('page-el')
const scrollRoot = useParentElement()
const { user, isAuthorized } = storeToRefs(useUserStore())
const { pdf, print, pages } = usePDF(dnLink, {
  getHttpHeaders: () => {
    if (isAuthorized.value && user.value?.access_token) {
      return { Authorization: `Bearer ${user.value.access_token}` }
    }
    return {}
  },
})
defineExpose({ pages, jumpTo, print })
onMounted(() => {
  observerInit()
})
onBeforeUnmount(() => {
  observer.disconnect()
  observer = null
})
function observerInit() {
  if (observer) {
    observer.disconnect()
    observer = null
  }
  observer = new IntersectionObserver(onIntersect, { threshold: 0.08, root: scrollRoot.value })
}
function onIntersect(entries) {
  if (!intersecting) {
    return
  }
  for (let entry of entries) {
    if (entry.isIntersecting) {
      const i = Array.prototype.indexOf.call(pagesCanvaEl.value.children, entry.target)
      pageCurrent.value = i + 1
      emit('page', pageCurrent.value, pages.value)
    }
  }
}
function onPageLoaded(e) {
  pageLoaded++
  if (pageLoaded == 1) {
    firstViewport = e
  }
  if (pageLoaded == pages.value) {
    emit('loaded', pages.value, e.scale, pageOrientation())
    pageLoaded = 0
    firstViewport = null
    observerInit()
    for (let p of pageEl.value) {
      observer.observe(p)
    }
  }
}
function pageOrientation() {
  if (!firstViewport || !firstViewport.width || !firstViewport.height) {
    return null
  }
  let landscape = firstViewport.width > firstViewport.height
  if (props.rotation % 180 !== 0) {
    landscape = !landscape
  }
  return landscape ? 'landscape' : 'portrait'
}
function jumpTo(n) {
  let pg = pageEl.value?.[n - 1]
  if (pg == undefined) {
    return
  }
  intersecting = false
  scrollTo(scrollRoot.value, pg.offsetTop, () => (intersecting = true))
}
</script>
<template>
  <div
    class="vstack gap-3 m-3 doc-view-pdf"
    ref="pages-canva"
  >
    <div
      class="border align-self-center shadow"
      v-for="p of pages"
      :key="p"
      ref="page-el"
    >
      <VuePDF
        :pdf="pdf"
        :page="p"
        :scale="scale"
        :rotation="rotation"
        @loaded="onPageLoaded"
      />
    </div>
  </div>
</template>
